import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { BadgeMultiSelect } from "@/components/BadgeMultiSelect";
import { toast } from "sonner";
import { MoneyInput } from "@/components/MoneyInput";

interface CapitalProviderEnrichReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: { id: string; name: string };
  enrichResult: {
    website?: any;
    apollo?: any;
    current?: any;
    websiteError?: string;
  };
  onApplied: () => void;
}

const CAPITAL_TYPE_OPTIONS = [
  "Senior Debt", "Unitranche", "Mezzanine", "Subordinated Debt",
  "Preferred Equity", "Common Equity", "Growth Equity", "Venture Debt",
];

const COLLATERAL_OPTIONS = [
  "Senior Lien", "Cash Flow", "ABL", "Real Estate", "Equipment",
];

export function CapitalProviderEnrichReview({
  open, onOpenChange, company, enrichResult, onApplied,
}: CapitalProviderEnrichReviewProps) {
  const website = enrichResult.website || {};
  const apollo = enrichResult.apollo || {};

  // Local editable state, seeded from enrichResult on open
  const [description, setDescription] = useState("");
  const [headquarters, setHeadquarters] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [firmType, setFirmType] = useState("");
  const [capitalTypes, setCapitalTypes] = useState<string[]>([]);
  const [minDealSize, setMinDealSize] = useState("");
  const [maxDealSize, setMaxDealSize] = useState("");
  const [sweetSpot, setSweetSpot] = useState("");
  const [minEbitda, setMinEbitda] = useState("");
  const [industryFocus, setIndustryFocus] = useState("");
  const [industryExclusions, setIndustryExclusions] = useState("");
  const [geographicFocus, setGeographicFocus] = useState("");
  const [collateralTypes, setCollateralTypes] = useState<string[]>([]);
  const [typicalRate, setTypicalRate] = useState("");
  const [typicalLeverage, setTypicalLeverage] = useState("");
  const [structureNotes, setStructureNotes] = useState("");
  const [speedToClose, setSpeedToClose] = useState("");
  const [applying, setApplying] = useState(false);

  // Seed state whenever modal opens with new data
  useEffect(() => {
    if (!open) return;
    setDescription(website.investmentProfile || apollo.description || "");
    setHeadquarters(apollo.headquarters || "");
    setLinkedinUrl(apollo.linkedinUrl || "");
    setEmployeeCount(apollo.employeeCount || "");
    setFirmType(website.firmType || "");
    setCapitalTypes(website.capitalTypes || []);
    setMinDealSize(website.minDealSize || "");
    setMaxDealSize(website.maxDealSize || "");
    setSweetSpot(website.sweetSpot || "");
    setMinEbitda(website.minEbitda || "");
    setIndustryFocus((website.industryFocus || []).join(", "));
    setIndustryExclusions((website.industryExclusions || []).join(", "));
    setGeographicFocus(website.geographicFocus || "");
    setCollateralTypes(website.collateralTypes || []);
    setTypicalRate(website.typicalRate || "");
    setTypicalLeverage(website.typicalLeverage || "");
    setStructureNotes(website.structureNotes || "");
    setSpeedToClose(website.speedToClose || "");
  }, [open, enrichResult]);

  const splitList = (s: string): string[] | null => {
    const items = s.split(",").map(x => x.trim()).filter(Boolean);
    return items.length > 0 ? items : null;
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const fields: any = {
        description: description || undefined,
        headquarters: headquarters || undefined,
        linkedinUrl: linkedinUrl || undefined,
        employeeCount: employeeCount || undefined,
        firmType: firmType || undefined,
        capitalTypes: capitalTypes.length > 0 ? capitalTypes : null,
        minDealSize: minDealSize || null,
        maxDealSize: maxDealSize || null,
        sweetSpot: sweetSpot || null,
        minEbitda: minEbitda || null,
        industryFocus: splitList(industryFocus),
        industryExclusions: splitList(industryExclusions),
        geographicFocus: geographicFocus || null,
        collateralTypes: collateralTypes.length > 0 ? collateralTypes : null,
        typicalRate: typicalRate || null,
        typicalLeverage: typicalLeverage || null,
        structureNotes: structureNotes || null,
        speedToClose: speedToClose || null,
      };

      const res = await fetch(`/api/companies/${company.id}/apply-enrichment`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fields,
          domain: apollo.website || null,
          apolloId: apollo.apolloId || null,
          phone: apollo.phone || null,
          foundedYear: apollo.foundedYear || null,
          fundingHistory: apollo.fundingHistory || [],
          totalFunding: apollo.totalFunding || null,
          linkedinUrl: apollo.linkedinUrl || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || "Failed to apply enrichment");
        return;
      }

      onApplied();
      onOpenChange(false);
    } catch {
      toast.error("Failed to apply enrichment");
    } finally {
      setApplying(false);
    }
  };

  const hasAnyData = Boolean(
    enrichResult.website || enrichResult.apollo
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Review Enrichment: {company.name}
            {website.confidence && (
              <Badge variant="outline" className="text-xs">
                {website.confidence} confidence
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Review the extracted fields. Edit anything that's wrong, then click Apply.
          </DialogDescription>
        </DialogHeader>

        {enrichResult.websiteError && (
          <div className="flex items-start gap-2 p-3 border border-amber-300 bg-amber-50 rounded text-sm dark:border-amber-800 dark:bg-amber-950/40">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0 dark:text-amber-400" />
            <div><strong>Website issue:</strong> {enrichResult.websiteError}</div>
          </div>
        )}

        {!hasAnyData && !enrichResult.websiteError && (
          <div className="text-sm text-muted-foreground">
            No enrichment data found for this company.
          </div>
        )}

        {hasAnyData && (
          <div className="space-y-5">
            {/* Source badges */}
            <div className="flex items-center gap-2 text-xs">
              {enrichResult.website && <Badge variant="secondary">Website</Badge>}
              {enrichResult.apollo && <Badge variant="secondary">Apollo</Badge>}
            </div>

            {/* Company basics */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="cp-description">Description</Label>
                <Textarea
                  id="cp-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cp-hq">Headquarters</Label>
                  <Input id="cp-hq" value={headquarters} onChange={(e) => setHeadquarters(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cp-linkedin">LinkedIn URL</Label>
                  <Input id="cp-linkedin" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cp-employees">Employee Count</Label>
                  <Input id="cp-employees" value={employeeCount} onChange={(e) => setEmployeeCount(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cp-firmtype">Firm Type</Label>
                  <Input id="cp-firmtype" value={firmType} onChange={(e) => setFirmType(e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>

            {/* CP criteria */}
            <div className="space-y-3 border-t pt-4">
              <h3 className="text-sm font-semibold">Capital Criteria</h3>

              <div>
                <Label>Capital Types</Label>
                <BadgeMultiSelect
                  selected={capitalTypes}
                  options={CAPITAL_TYPE_OPTIONS}
                  placeholder="Select capital types..."
                  onSave={setCapitalTypes}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cp-mindeal">Min Deal Size</Label>
                  <MoneyInput id="cp-mindeal" kind="dealSize" value={minDealSize} onValueChange={setMinDealSize} placeholder="$5M" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cp-maxdeal">Max Deal Size</Label>
                  <MoneyInput id="cp-maxdeal" kind="dealSize" value={maxDealSize} onValueChange={setMaxDealSize} placeholder="$100M" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cp-sweet">Sweet Spot</Label>
                  <Input id="cp-sweet" value={sweetSpot} onChange={(e) => setSweetSpot(e.target.value)} placeholder="$10M-$25M" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cp-ebitda">Min EBITDA</Label>
                  <MoneyInput id="cp-ebitda" kind="lenderEbitda" value={minEbitda} onValueChange={setMinEbitda} placeholder="$3M" className="mt-1" />
                </div>
              </div>

              <div>
                <Label htmlFor="cp-industries">Industry Focus (comma-separated)</Label>
                <Input id="cp-industries" value={industryFocus} onChange={(e) => setIndustryFocus(e.target.value)} placeholder="Healthcare, Software, Business Services" className="mt-1" />
              </div>

              <div>
                <Label htmlFor="cp-exclusions">Industry Exclusions (comma-separated)</Label>
                <Input id="cp-exclusions" value={industryExclusions} onChange={(e) => setIndustryExclusions(e.target.value)} placeholder="Tobacco, Firearms" className="mt-1" />
              </div>

              <div>
                <Label htmlFor="cp-geo">Geographic Focus</Label>
                <Input id="cp-geo" value={geographicFocus} onChange={(e) => setGeographicFocus(e.target.value)} placeholder="North America" className="mt-1" />
              </div>

              <div>
                <Label>Collateral Types</Label>
                <BadgeMultiSelect
                  selected={collateralTypes}
                  options={COLLATERAL_OPTIONS}
                  placeholder="Select collateral types..."
                  onSave={setCollateralTypes}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cp-rate">Typical Rate</Label>
                  <Input id="cp-rate" value={typicalRate} onChange={(e) => setTypicalRate(e.target.value)} placeholder="SOFR + 6-8" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cp-leverage">Typical Leverage</Label>
                  <Input id="cp-leverage" value={typicalLeverage} onChange={(e) => setTypicalLeverage(e.target.value)} placeholder="Up to 4.5x" className="mt-1" />
                </div>
              </div>

              <div>
                <Label htmlFor="cp-structure">Structure Notes</Label>
                <Textarea id="cp-structure" value={structureNotes} onChange={(e) => setStructureNotes(e.target.value)} rows={2} className="mt-1" />
              </div>

              <div>
                <Label htmlFor="cp-speed">Speed to Close</Label>
                <Input id="cp-speed" value={speedToClose} onChange={(e) => setSpeedToClose(e.target.value)} placeholder="30-45 days" className="mt-1" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={applying || !hasAnyData}>
            {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
